import React, { useCallback, useEffect, useState } from 'react';
import { requestJson } from '../utils/authClient.js';
import { AdminSection } from '../components/AdminNav.jsx';

// The company read as a game over communication behaviour (IDD D-VCORE-294, D-HP-ADMIN-050, D-HP-ADMIN-052): the
// players are the positions, a strategy is how a position communicates (answer when addressed, hand over once and
// cleanly, substance instead of noise, topics carried to results), the payoff is truth delivered net of noise —
// Veridion's vision made measurable. A Nash equilibrium is the state in which no position improves the team's
// delivered truth by deviating alone. Closed-loop enforcement feeds utility and noise back into effective relevance
// thresholds, handover hop budgets, and Scrum ceremony commitments in pAIoneer-scrum.
const API = '/api/admin/veridion-core';
const REFRESH_MS = 20000;
const call = (path) => requestJson(`${API}${path}`);
const post = (path, body) => requestJson(`${API}${path}`, { method: 'POST', body: JSON.stringify(body) });
const num = (value) => (value === null || value === undefined || value === '' ? '—' : Number(value).toLocaleString());
const fill = (text, values) => Object.entries(values).reduce((out, [key, value]) => out.replace(`{${key}}`, value), String(text || ''));

const noiseOf = (noise) => (
  noise
    ? (noise.withheld || 0) + (noise.repeats || 0) + (noise.deadAir || 0) + (noise.unanswered || 0) + (noise.excessHops || 0)
    : 0
);

// A thin single-hue bar for a magnitude (the burndown's remaining points, a sprint's velocity): the value stands as
// text beside it, the track is the surface, the fill the accent; one hue, because the job is magnitude, not identity.
const Bar = ({ value, max, label, detail, tone }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="text-text-secondary" style={{ minWidth: '7.5em' }}>{label}</span>
    <span className="flex-1" style={{ background: 'var(--bg-secondary)', borderRadius: '0.25rem', height: '0.625rem', overflow: 'hidden' }}>
      <span style={{ display: 'block', height: '100%', borderRadius: '0 0.25rem 0.25rem 0', width: `${max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0}%`, background: tone === 'error' ? 'var(--ink-soft)' : 'var(--accent)' }} />
    </span>
    <span style={{ minWidth: '3em' }}><strong>{num(value)}</strong></span>
    {detail ? <span className="text-text-secondary">{detail}</span> : null}
  </div>
);

const VeridionGameTheoryPanel = ({ t }) => {
  const g = (t.admin && t.admin.gt) || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [goal, setGoal] = useState(null);
  const [days, setDays] = useState(null);
  const load = useCallback(() => {
    call('/gametheory').then((result) => { setData(result); setError(''); })
      .catch((failure) => setError(failure?.code || failure?.status || 'unavailable'));
  }, []);
  useEffect(() => { load(); const timer = setInterval(load, REFRESH_MS); return () => clearInterval(timer); }, [load]);
  const act = (body) => { setBusy(true); post('/scrum', body).catch(() => null).finally(() => { setBusy(false); setGoal(null); setDays(null); load(); }); };

  if (error && !data) return <div className="homepage-muted-panel p-4 text-sm text-error">{g.unavailable || 'unavailable'} ({error})</div>;
  if (!data) return <div className="homepage-muted-panel p-4 text-sm">{g.loading || '…'}</div>;

  const players = data.players || [];
  const active = players.filter((player) => !player.standby || player.spoke || player.handoversOut || player.pausedTurns || player.closed || player.open);
  const edges = data.edges || [];
  const meetings = data.meetings || {};
  const time = data.time || {};
  const deviations = data.deviations || [];
  const scrum = data.scrum || {};
  const sprint = scrum.active || null;
  const closedSprints = scrum.sprints || [];
  const unitBreakdown = Array.isArray(scrum.unitBreakdown) ? scrum.unitBreakdown : [];
  const ceremonySpace = scrum.ceremonySpace || (scrum.settings && scrum.settings.ceremonySpace) || 'pAIoneer-scrum';
  const lastSos = scrum.lastScrumOfScrums || null;
  const escalatedImpediments = Array.isArray(scrum.escalatedImpediments)
    ? scrum.escalatedImpediments
    : (lastSos && Array.isArray(lastSos.escalatedImpediments) ? lastSos.escalatedImpediments : []);
  const maxVelocity = Math.max(1, ...closedSprints.map((s) => s.velocity || 0), sprint ? sprint.committedPoints : 0);
  const maxRemaining = sprint ? Math.max(1, sprint.committedPoints || 0) : 1;
  const nameOf = (key) => { const found = players.find((player) => player.key === key); return found ? found.name : key; };
  const meetingsLine = g.meetingsLine
    ? String(g.meetingsLine).replace('{held}', num(meetings.held)).replace('{turns}', num(meetings.turns)).replace('{u}', num(meetings.unanswered))
    : '';

  return (
    <div className="space-y-6">
      <AdminSection id="gt-definition" title={g.defTitle} subtitle={g.defSubtitle}>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="homepage-muted-panel p-3"><div className="font-bold text-sm">{g.defPlayers}</div><p className="text-sm text-text-secondary">{g.defPlayersText}</p></div>
          <div className="homepage-muted-panel p-3"><div className="font-bold text-sm">{g.defStrategies}</div><p className="text-sm text-text-secondary">{g.defStrategiesText}</p></div>
          <div className="homepage-muted-panel p-3"><div className="font-bold text-sm">{g.defPayoff}</div><p className="text-sm text-text-secondary">{g.defPayoffText}</p></div>
          <div className="homepage-muted-panel p-3"><div className="font-bold text-sm">{g.defEquilibrium}</div><p className="text-sm text-text-secondary">{g.defEquilibriumText}</p></div>
        </div>
      </AdminSection>

      <AdminSection id="gt-scrum" title={g.scrumTitle} subtitle={g.scrumSubtitle} state={sprint ? fill(g.sprintLabel, { n: sprint.number }) : ceremonySpace}>
        <div className="text-xs text-text-secondary mb-2">
          <span>{g.ceremonySpaceLabel}: </span>
          <strong>{ceremonySpace}</strong>
        </div>
        {sprint ? (
          <>
            <div className="grid gap-3 md:grid-cols-4">
              <div className="homepage-muted-panel p-3"><div className="text-xs text-text-secondary">{fill(g.sprintLabel, { n: sprint.number })}</div><div className="text-sm font-bold">{sprint.goal}</div><div className="text-xs text-text-secondary">{g.ends} {new Date(sprint.endsAt).toLocaleString()}</div></div>
              <div className="homepage-muted-panel p-3"><div className="text-xs text-text-secondary">{g.committedPts}</div><div className="text-lg font-bold">{num(sprint.committedPoints)}</div></div>
              <div className="homepage-muted-panel p-3"><div className="text-xs text-text-secondary">{g.deliveredPts}</div><div className="text-lg font-bold">{num(sprint.deliveredPoints)}</div></div>
              <div className="homepage-muted-panel p-3"><div className="text-xs text-text-secondary">{g.remainingPts}</div><div className="text-lg font-bold">{num(sprint.remainingPoints)}</div></div>
            </div>
            {(sprint.burndown || []).length ? (
              <div className="mt-3 space-y-1">
                <div className="text-xs font-bold">{g.burndownTitle}</div>
                {sprint.burndown.map((day) => <Bar key={day.day} value={day.remaining} max={maxRemaining} label={day.day} />)}
              </div>
            ) : null}
            {(sprint.items || []).length ? (
              <div className="admin-table-shell mt-3"><div className="admin-table-scroll">
                <table className="admin-table">
                  <thead><tr><th>{g.colItem}</th><th>{g.colOwner}</th><th>{g.colPoints}</th><th>{g.colState}</th></tr></thead>
                  <tbody>
                    {sprint.items.map((item) => (
                      <tr key={item.item}>
                        <td>{item.title}{item.addedBy ? <div className="text-xs text-text-secondary">{fill(g.addedBy, { who: item.addedBy })}</div> : null}</td>
                        <td>{nameOf(item.owner)}</td>
                        <td>{item.points === null || item.points === undefined ? g.unestimated : num(item.points)}</td>
                        <td className={item.done ? '' : 'text-text-secondary'}>{item.done ? g.itemDone : g.itemOpen}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            ) : <p className="text-sm text-text-secondary mt-3">{g.noItems}</p>}
          </>
        ) : <p className="text-sm text-text-secondary">{g.noSprint}</p>}
        {unitBreakdown.length ? (
          <div className="mt-4">
            <div className="text-xs font-bold mb-1">{g.unitBreakdownTitle}</div>
            <div className="admin-table-shell"><div className="admin-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{g.colUnit}</th>
                    <th>{g.committedPts}</th>
                    <th>{g.deliveredPts}</th>
                    <th>{g.colWip}</th>
                    <th>{g.colImpediments}</th>
                  </tr>
                </thead>
                <tbody>
                  {unitBreakdown.map((unit, idx) => {
                    const impedimentCount = Array.isArray(unit.impediments) ? unit.impediments.length : (unit.impediments ?? 0);
                    return (
                      <tr key={unit.key || unit.unit || unit.name || idx}>
                        <td><strong>{unit.name || unit.unit || unit.key}</strong></td>
                        <td>{num(unit.committedPoints ?? 0)}</td>
                        <td>{num(unit.deliveredPoints ?? 0)}</td>
                        <td>{num(unit.openWip ?? unit.wip ?? unit.openItems ?? 0)}</td>
                        <td className={impedimentCount > 0 ? 'text-error' : ''}>{num(impedimentCount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div></div>
          </div>
        ) : null}
        {lastSos ? (
          <div className="homepage-muted-panel p-3 mt-3 text-xs space-y-1">
            <div className="font-bold">
              {g.lastScrumOfScrumsTitle}
              {typeof lastSos === 'object' && lastSos.heldAt ? ` · ${new Date(lastSos.heldAt).toLocaleString()}` : ''}
            </div>
            <p className="text-text-secondary">
              {typeof lastSos === 'string' ? lastSos : (lastSos.summary || '')}
            </p>
          </div>
        ) : null}
        {escalatedImpediments.length ? (
          <div className="mt-3 space-y-1">
            <div className="text-xs font-bold text-error">{g.escalatedImpedimentsTitle}</div>
            <ul className="space-y-1 text-xs">
              {escalatedImpediments.map((imp, idx) => (
                <li key={idx} className="homepage-muted-panel p-2">
                  {typeof imp === 'string' ? imp : `${imp.unit ? `${imp.unit}: ` : ''}${imp.detail || imp.title || ''}`}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {closedSprints.length ? (
          <div className="mt-4 space-y-1">
            <div className="text-xs font-bold">{g.velocityTitle}</div>
            {closedSprints.map((s) => (
              <Bar key={s.number} value={s.velocity || 0} max={maxVelocity} label={fill(g.sprintLabel, { n: s.number })}
                   detail={s.predictability === null || s.predictability === undefined ? '' : `${g.predictability} ${Math.round(s.predictability * 100)}%`} />
            ))}
          </div>
        ) : null}
        <p className="text-xs text-text-secondary mt-2">{fill(g.forecastLine, { v: num(scrum.velocity), b: num(scrum.backlogPoints), s: num(scrum.forecastSprints) })}</p>
        <div className="flex flex-wrap items-end gap-2 mt-3">
          {sprint ? (
            <>
              <label className="text-xs text-text-secondary">{g.setGoal}
                <input className="form-input block" type="text" maxLength={300} value={goal === null ? (sprint.goal || '') : goal}
                       onChange={(event) => setGoal(event.target.value)} />
              </label>
              <label className="text-xs text-text-secondary">{g.sprintDaysLabel}
                <input className="form-input block" type="number" min={1} max={28} style={{ width: '5em' }}
                       value={days === null ? ((scrum.settings || {}).sprintDays || 1) : days}
                       onChange={(event) => setDays(event.target.value)} />
              </label>
              <button type="button" className="homepage-button-secondary" disabled={busy}
                      onClick={() => act({ action: 'set', ...(goal !== null ? { goal } : {}), ...(days !== null ? { sprintDays: Number(days) } : {}) })}>{g.save}</button>
              <button type="button" className="homepage-button-secondary" disabled={busy}
                      onClick={() => window.confirm(g.confirmScrumOfScrums) && act({ action: 'scrum_of_scrums' })}>{g.scrumOfScrums}</button>
              <button type="button" className="homepage-button-secondary is-destructive" disabled={busy}
                      onClick={() => window.confirm(g.confirmEndSprint) && act({ action: 'end_sprint' })}>{g.endSprint}</button>
            </>
          ) : (
            <>
              <button type="button" className="homepage-button-secondary" disabled={busy}
                      onClick={() => window.confirm(g.confirmPlanNow) && act({ action: 'plan_now' })}>{g.planNow}</button>
              <button type="button" className="homepage-button-secondary" disabled={busy}
                      onClick={() => window.confirm(g.confirmScrumOfScrums) && act({ action: 'scrum_of_scrums' })}>{g.scrumOfScrums}</button>
            </>
          )}
        </div>
      </AdminSection>

      <AdminSection id="gt-players" title={g.playersTitle} subtitle={g.playersSubtitle}>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{g.player}</th>
                <th>{g.contributions}</th>
                <th>{g.meetingTurns}</th>
                <th>{g.handovers}</th>
                <th>{g.delivery}</th>
                <th title={g.pausedTurnsHint}>{g.pausedTurns}</th>
                <th>{g.closedOpen}</th>
                <th>{g.sprintPointsCol}</th>
                <th>{g.noise}</th>
                <th>{g.effectiveThresholdCol}</th>
                <th>{g.utility}</th>
              </tr>
            </thead>
            <tbody>
              {active.map((player) => {
                const excessHops = (player.noise && player.noise.excessHops) || player.excessHops || 0;
                const effThreshold = player.effectiveThreshold;
                const baseThreshold = player.baseThreshold ?? player.threshold;
                return (
                  <tr key={player.key} className={player.utility < 0 ? 'text-error' : ''}>
                    <td><strong>{player.name}</strong><div className="text-xs text-text-secondary">{player.unit}{player.standby ? ` · ${g.standby}` : ''}</div></td>
                    <td>{num(player.spoke)}</td>
                    <td>{num(player.meetingTurns)}</td>
                    <td>{num(player.handoversOut)}{player.handoversOut ? <span className="text-xs text-text-secondary"> · {g.consumedShort} {num(player.consumedOut)}{player.refusedOut ? ` · ${g.refusedShort} ${num(player.refusedOut)}` : ''}</span> : null}</td>
                    <td>{player.deliveryMedianMin === null || player.deliveryMedianMin === undefined ? '—' : `${player.deliveryMedianMin} min`}</td>
                    <td>{num(player.pausedTurns ?? 0)}</td>
                    <td>{num(player.closed)} / {num(player.open)}</td>
                    <td>{player.committedPoints ? `${num(player.deliveredPoints)} / ${num(player.committedPoints)}` : '—'}</td>
                    <td title={player.noise ? `${g.devWithheld}: ${player.noise.withheld || 0} · ${g.devRepetition}: ${player.noise.repeats || 0} · ${g.devDeadAir}: ${player.noise.deadAir || 0} · ${g.devMeetingUnanswered}: ${player.noise.unanswered || 0} · ${g.excessHopsLabel}: ${excessHops}` : ''}>
                      {num(noiseOf(player.noise))}
                      {excessHops ? <div className="text-xs text-error">{g.excessHopsLabel}: {num(excessHops)}</div> : null}
                    </td>
                    <td>
                      {effThreshold === null || effThreshold === undefined
                        ? '—'
                        : (baseThreshold !== null && baseThreshold !== undefined && effThreshold !== baseThreshold
                          ? `${num(effThreshold)} (${num(baseThreshold)})`
                          : num(effThreshold))}
                    </td>
                    <td><strong>{num(player.utility)}</strong></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div></div>
        {meetingsLine ? <p className="text-xs text-text-secondary mt-2">{meetingsLine}</p> : null}
        <p className="text-xs text-text-secondary mt-1">{g.playersHint}</p>
      </AdminSection>

      <AdminSection id="gt-interactions" title={g.interactionsTitle} subtitle={g.interactionsSubtitle} count={edges.length}>
        {edges.length ? (
          <div className="admin-table-shell"><div className="admin-table-scroll">
            <table className="admin-table">
              <thead><tr><th>{g.from}</th><th>{g.to}</th><th>{g.messages}</th><th>{g.delivery}</th><th>{g.consumed}</th><th>{g.refused}</th></tr></thead>
              <tbody>
                {edges.map((edge, index) => (
                  <tr key={index}>
                    <td><strong>{nameOf(edge.from)}</strong></td>
                    <td>{nameOf(edge.to)}</td>
                    <td>{num(edge.count)}</td>
                    <td>{edge.medianMin === null || edge.medianMin === undefined ? '—' : `${edge.medianMin} min`}</td>
                    <td>{num(edge.consumed)}</td>
                    <td className={edge.refused ? 'text-error' : ''}>{num(edge.refused)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></div>
        ) : <p className="text-sm text-text-secondary">{g.interactionsEmpty}</p>}
        <p className="text-xs text-text-secondary mt-2">{g.interactionsHint}</p>
      </AdminSection>

      <AdminSection id="gt-deviations" title={g.deviationsTitle} subtitle={g.deviationsSubtitle} count={deviations.length} tone={deviations.length ? 'error' : 'ok'}>
        {deviations.length ? (
          <ul className="space-y-1 text-sm">
            {deviations.map((deviation, index) => (
              <li key={index} className="homepage-muted-panel p-2">
                <span className="text-xs font-bold text-error">{g[`dev_${deviation.kind}`] || deviation.kind}</span>
                {deviation.player ? <span className="text-xs"> · {nameOf(deviation.player)}</span> : null} · {deviation.detail}
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-text-secondary">{g.equilibrium}</p>}
      </AdminSection>

      <AdminSection id="gt-time" title={g.timeTitle} subtitle={g.timeSubtitle}>
        <div className="grid gap-3 md:grid-cols-3">
          <div className="homepage-muted-panel p-3"><div className="text-xs text-text-secondary">{g.avgCycle}</div><div className="text-lg font-bold">{time.avgCycleHours ? `${time.avgCycleHours} h` : '—'}</div><div className="text-xs text-text-secondary">{g.fromTasks ? String(g.fromTasks).replace('{n}', num(time.completed)) : ''}</div></div>
          <div className="homepage-muted-panel p-3"><div className="text-xs text-text-secondary">{g.speedup}</div><div className="text-lg font-bold">{time.speedup ? `${time.speedup}×` : '—'}</div><div className="text-xs text-text-secondary">{g.speedupHint ? String(g.speedupHint).replace('{h}', num(time.humanHoursPerItem)) : ''}</div></div>
          <div className="homepage-muted-panel p-3"><div className="text-xs text-text-secondary">{g.humanDelivered}</div><div className="text-lg font-bold">{time.humanDaysDelivered ?? '—'} {g.humanDays}</div><div className="text-xs text-text-secondary">{g.forecast ? String(g.forecast).replace('{d}', time.forecastHumanDays ?? '—') : ''}</div></div>
        </div>
        {time.sizes && (time.sizes.small || time.sizes.medium || time.sizes.large) ? (
          <p className="text-xs text-text-secondary mt-3">{g.sizes}: {g.sizeSmall} {num(time.sizes.small)} · {g.sizeMedium} {num(time.sizes.medium)} · {g.sizeLarge} {num(time.sizes.large)}</p>
        ) : null}
        <p className="text-xs text-text-secondary mt-2">{g.timeHint}</p>
      </AdminSection>
    </div>
  );
};

export default VeridionGameTheoryPanel;
